const UPDATE_RECEIPT_KEY = "papa-gps-update";
const UPDATE_TIMEOUT_MS = 20_000;
const RECEIPT_MAX_AGE_MS = 10 * 60_000;
const AUTO_CHECK_INTERVAL_MS = 60_000;

interface UpdateOptions {
  buildId: string;
  serviceWorker?: ServiceWorkerContainer;
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  reload?: () => void;
}

/** Follow the service worker activation and carry its result across the reload. */
export function setupAppUpdate(options: UpdateOptions): { watch: (registration: ServiceWorkerRegistration) => void; dispose: () => void } {
  const banner = document.getElementById("update-banner")!;
  const message = document.getElementById("update-message")!;
  const button = document.getElementById("update-button") as HTMLButtonElement;
  const spinner = document.getElementById("update-spinner")!;
  const version = document.getElementById("app-version")!;
  const checkButton = document.getElementById("check-update-button") as HTMLButtonElement;
  const checkStatus = document.getElementById("update-check-status")!;
  const serviceWorker = options.serviceWorker ?? navigator.serviceWorker;
  const reload = options.reload ?? (() => window.location.reload());
  let registration: ServiceWorkerRegistration | undefined;
  let state: "available" | "updating" | "success" | "error" = "available";
  let timeout: number | undefined;
  let stopWatchingController: (() => void) | undefined;
  let attempt = 0;
  let checkController: AbortController | undefined;
  let manualCheck = false;
  let lastCheckAt = -Infinity;
  let pageNeedsReload = false;
  let pageController = serviceWorker?.controller;
  const lifecycle = new AbortController();
  const buildDate = new Date(options.buildId);
  version.textContent = Number.isNaN(buildDate.getTime())
    ? "Version de développement"
    : `Version du ${buildDate.toLocaleDateString("fr-FR")} à ${buildDate.toLocaleTimeString("fr-FR")}`;
  version.title = options.buildId;
  let storage: UpdateOptions["storage"];
  try { storage = options.storage ?? window.sessionStorage; } catch { /* Updates also work with storage disabled. */ }

  function show(next: typeof state, text: string): void {
    state = next;
    banner.dataset.state = next;
    message.textContent = text;
    button.disabled = next === "updating";
    button.textContent = { available: "Mettre à jour", updating: "Mise à jour en cours…", success: "Compris", error: "Réessayer" }[next];
    spinner.hidden = next !== "updating";
    banner.hidden = false;
    refreshCheckButton();
  }

  function refreshCheckButton(): void {
    const visibleCheck = Boolean(checkController) && manualCheck;
    checkButton.disabled = state === "updating" || visibleCheck;
    checkButton.textContent = visibleCheck ? "Vérification en cours…" : "Vérifier les mises à jour";
  }

  function reportCheck(text: string, error = false): void {
    checkStatus.textContent = text;
    checkStatus.dataset.state = error ? "error" : "info";
  }

  function announceUpdate(): void {
    show("available", "Une nouvelle version est prête à être installée.");
    reportCheck("Nouvelle version disponible.");
  }

  function waitForInstallation(worker: ServiceWorker, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const finish = () => {
        if (!signal.aborted && !["installed", "activating", "activated", "redundant"].includes(worker.state)) return;
        worker.removeEventListener("statechange", finish);
        signal.removeEventListener("abort", finish);
        if (signal.aborted || worker.state === "redundant") reject(new Error("Installation unavailable"));
        else resolve();
      };
      worker.addEventListener("statechange", finish);
      signal.addEventListener("abort", finish);
      finish();
    });
  }

  async function checkForUpdates(manual = false): Promise<void> {
    if (!registration || state === "updating" || lifecycle.signal.aborted) return;
    // A click can make an automatic check visible without starting a second request.
    if (checkController) {
      if (manual) {
        manualCheck = true;
        reportCheck("Vérification en cours…");
        refreshCheckButton();
      }
      return;
    }
    if (!manual && (document.visibilityState === "hidden" || Date.now() - lastCheckAt < AUTO_CHECK_INTERVAL_MS)) return;
    if (registration.waiting || pageNeedsReload) {
      announceUpdate();
      return;
    }
    if (!navigator.onLine) {
      if (manual) reportCheck("Sans connexion, impossible de vérifier les mises à jour.", true);
      return;
    }
    lastCheckAt = Date.now();
    const controller = new AbortController();
    checkController = controller;
    manualCheck = manual;
    refreshCheckButton();
    if (manual) reportCheck("Vérification en cours…");
    else reportCheck("");
    const checkTimeout = window.setTimeout(() => controller.abort(), UPDATE_TIMEOUT_MS);
    try {
      await Promise.race([
        (async () => {
          await registration.update();
          if (controller.signal.aborted) throw new Error("Check expired");
          // update() may resolve before the new application's files finish downloading.
          if (registration.installing) await waitForInstallation(registration.installing, controller.signal);
        })(),
        new Promise<never>((_, reject) => controller.signal.addEventListener("abort", () => reject(new Error("Check cancelled")), { once: true }))
      ]);
      if (lifecycle.signal.aborted) return;
      if (registration.waiting || pageNeedsReload) announceUpdate();
      else if (manualCheck) reportCheck("Votre application est à jour.");
    } catch {
      if (manualCheck && !lifecycle.signal.aborted && controller.signal.reason !== "activation") reportCheck("Vérification impossible. Vérifiez votre connexion et réessayez.", true);
    } finally {
      window.clearTimeout(checkTimeout);
      controller.abort();
      checkController = undefined;
      refreshCheckButton();
    }
  }

  const automaticCheck = () => { void checkForUpdates(); };
  checkButton.addEventListener("click", () => { void checkForUpdates(true); }, { signal: lifecycle.signal });

  function clearReceipt(): void {
    try { storage?.removeItem(UPDATE_RECEIPT_KEY); } catch { /* Storage can be unavailable. */ }
  }

  function cleanup(): void {
    window.clearTimeout(timeout);
    stopWatchingController?.();
    stopWatchingController = undefined;
  }

  function fail(): void {
    attempt += 1;
    cleanup();
    clearReceipt();
    show("error", "La mise à jour n’a pas abouti. Vous pouvez réessayer.");
  }

  function reloadUpdatedApp(activated = false): void {
    attempt += 1;
    cleanup();
    message.textContent = activated ? "Nouvelle version activée. Rechargement de l’application…" : "Chargement de la nouvelle version…";
    try { reload(); } catch { fail(); }
  }

  function activateWaitingWorker(): boolean {
    const worker = registration?.waiting;
    if (!worker) return false;
    worker.postMessage({ type: "SKIP_WAITING" });
    return true;
  }

  function watchInstallingWorker(worker: ServiceWorker): void {
    const onStateChange = () => {
      if (worker.state === "redundant") {
        worker.removeEventListener("statechange", onStateChange);
        if (state === "updating") fail();
        return;
      }
      if (worker.state !== "installed") return;
      // Its later retirement is normal: it must not fail the next worker's update.
      worker.removeEventListener("statechange", onStateChange);
      if (!serviceWorker?.controller) return;
      if (state === "updating") {
        try { activateWaitingWorker(); } catch { fail(); }
      } else if (state !== "success") {
        announceUpdate();
      }
    };
    worker.addEventListener("statechange", onStateChange, { signal: lifecycle.signal });
    onStateChange();
  }

  // The old page records its build. Only a different loaded build confirms success.
  try {
    const raw = storage?.getItem(UPDATE_RECEIPT_KEY);
    clearReceipt();
    if (raw) {
      const receipt = JSON.parse(raw) as { from?: unknown; requestedAt?: unknown };
      const age = typeof receipt.requestedAt === "number" ? Date.now() - receipt.requestedAt : Infinity;
      if (typeof receipt.from === "string" && age >= 0 && age < RECEIPT_MAX_AGE_MS) {
        if (receipt.from !== options.buildId) show("success", "Application mise à jour. Vous utilisez la nouvelle version.");
        else show("error", "La nouvelle version n’a pas été chargée. Réessayez la mise à jour.");
      }
    }
  } catch { /* A missing or malformed receipt must not prevent using the app. */ }

  button.addEventListener("click", async () => {
    if (state === "success") {
      banner.hidden = true;
      state = "available";
      if (registration?.waiting) announceUpdate();
      return;
    }
    if (state === "updating") return;
    if (!registration || !serviceWorker) {
      show("error", "La mise à jour n’est pas encore prête. Réessayez dans un instant.");
      return;
    }
    const currentAttempt = ++attempt;
    checkController?.abort("activation");
    reportCheck("");
    show("updating", "Installation de la nouvelle version. L’application va se recharger.");
    try {
      storage?.setItem(UPDATE_RECEIPT_KEY, JSON.stringify({ from: options.buildId, requestedAt: Date.now() }));
    } catch { /* Keep the update available even when session storage is blocked. */ }
    const previousController = serviceWorker.controller;
    const onControllerChange = () => {
      if (state === "updating" && serviceWorker.controller && serviceWorker.controller !== previousController) reloadUpdatedApp(true);
    };
    serviceWorker.addEventListener("controllerchange", onControllerChange);
    stopWatchingController = () => serviceWorker.removeEventListener("controllerchange", onControllerChange);
    timeout = window.setTimeout(fail, UPDATE_TIMEOUT_MS);
    try {
      if (activateWaitingWorker()) return;
      // Another tab may already have activated the worker since the banner appeared.
      await registration.update();
      if (currentAttempt !== attempt) return;
      if (activateWaitingWorker() || registration.installing) return;
      reloadUpdatedApp();
    } catch {
      if (currentAttempt === attempt) fail();
    }
  }, { signal: lifecycle.signal });

  return {
    watch(nextRegistration) {
      registration = nextRegistration;
      checkButton.hidden = false;
      if (registration.waiting && state === "available") announceUpdate();
      if (registration.installing) watchInstallingWorker(registration.installing);
      registration.addEventListener("updatefound", () => {
        if (nextRegistration.installing) watchInstallingWorker(nextRegistration.installing);
      }, { signal: lifecycle.signal });
      window.addEventListener("focus", automaticCheck, { signal: lifecycle.signal });
      window.addEventListener("online", automaticCheck, { signal: lifecycle.signal });
      document.addEventListener("visibilitychange", automaticCheck, { signal: lifecycle.signal });
      serviceWorker?.addEventListener("controllerchange", () => {
        if (pageController && serviceWorker.controller !== pageController) {
          pageNeedsReload = true;
          if (state !== "updating") announceUpdate();
        }
        pageController = serviceWorker.controller;
      }, { signal: lifecycle.signal });
    },
    dispose() {
      lifecycle.abort();
      checkController?.abort();
      cleanup();
    }
  };
}
